import * as z from "zod/v4";
import { ShopifyIdType } from "./id";

// zod GID schemas, split out of ./id so that the zod-free id helpers
// (parseNumericIdFromShopifyId, convertShopifyIdToGid, the ShopifyIdType enum)
// can be imported — e.g. by the Shopify checkout UI extension bundle — without
// dragging in the zod runtime.

function _shopifyGidSchema<T extends ShopifyIdType>(type: T) {
  return z
    .string()
    .regex(
      new RegExp(`^gid://shopify/${type}/\\d+$`),
      `Invalid Shopify ${type} GID format`,
    );
}

const ShopifyIdSchemas = {
  [ShopifyIdType.Customer]: _shopifyGidSchema(ShopifyIdType.Customer),
  [ShopifyIdType.Order]: _shopifyGidSchema(ShopifyIdType.Order),
  [ShopifyIdType.Product]: _shopifyGidSchema(ShopifyIdType.Product),
  [ShopifyIdType.ProductVariant]: _shopifyGidSchema(
    ShopifyIdType.ProductVariant,
  ),
  [ShopifyIdType.Collection]: _shopifyGidSchema(ShopifyIdType.Collection),
  [ShopifyIdType.DraftOrder]: _shopifyGidSchema(ShopifyIdType.DraftOrder),
  [ShopifyIdType.LineItem]: _shopifyGidSchema(ShopifyIdType.LineItem),
  [ShopifyIdType.GiftCard]: _shopifyGidSchema(ShopifyIdType.GiftCard),
  [ShopifyIdType.SellingPlan]: _shopifyGidSchema(ShopifyIdType.SellingPlan),
  [ShopifyIdType.CalculatedOrder]: _shopifyGidSchema(
    ShopifyIdType.CalculatedOrder,
  ),
  [ShopifyIdType.CalculatedLineItem]: _shopifyGidSchema(
    ShopifyIdType.CalculatedLineItem,
  ),
  [ShopifyIdType.DeliveryProfile]: _shopifyGidSchema(
    ShopifyIdType.DeliveryProfile,
  ),
  [ShopifyIdType.Dispute]: _shopifyGidSchema(ShopifyIdType.Dispute),
  [ShopifyIdType.DisputeEvidence]: _shopifyGidSchema(
    ShopifyIdType.DisputeEvidence,
  ),
  [ShopifyIdType.MetafieldDefinition]: _shopifyGidSchema(
    ShopifyIdType.MetafieldDefinition,
  ),
  [ShopifyIdType.Article]: _shopifyGidSchema(ShopifyIdType.Article),
  [ShopifyIdType.Blog]: _shopifyGidSchema(ShopifyIdType.Blog),
  [ShopifyIdType.DeliveryCarrierService]: _shopifyGidSchema(
    ShopifyIdType.DeliveryCarrierService,
  ),
  [ShopifyIdType.DeliveryLocationGroup]: _shopifyGidSchema(
    ShopifyIdType.DeliveryLocationGroup,
  ),
  [ShopifyIdType.DeliveryZone]: _shopifyGidSchema(ShopifyIdType.DeliveryZone),
  [ShopifyIdType.DraftOrderLineItem]: _shopifyGidSchema(
    ShopifyIdType.DraftOrderLineItem,
  ),
  [ShopifyIdType.Duty]: _shopifyGidSchema(ShopifyIdType.Duty),
  [ShopifyIdType.EmailTemplate]: _shopifyGidSchema(ShopifyIdType.EmailTemplate),
  [ShopifyIdType.Fulfillment]: _shopifyGidSchema(ShopifyIdType.Fulfillment),
  [ShopifyIdType.FulfillmentEvent]: _shopifyGidSchema(
    ShopifyIdType.FulfillmentEvent,
  ),
  [ShopifyIdType.FulfillmentService]: _shopifyGidSchema(
    ShopifyIdType.FulfillmentService,
  ),
  [ShopifyIdType.InventoryItem]: _shopifyGidSchema(ShopifyIdType.InventoryItem),
  [ShopifyIdType.InventoryLevel]: _shopifyGidSchema(
    ShopifyIdType.InventoryLevel,
  ),
  [ShopifyIdType.Location]: _shopifyGidSchema(ShopifyIdType.Location),
  [ShopifyIdType.MarketingEvent]: _shopifyGidSchema(
    ShopifyIdType.MarketingEvent,
  ),
  [ShopifyIdType.MediaImage]: _shopifyGidSchema(ShopifyIdType.MediaImage),
  [ShopifyIdType.Metafield]: _shopifyGidSchema(ShopifyIdType.Metafield),
  [ShopifyIdType.OrderTransaction]: _shopifyGidSchema(
    ShopifyIdType.OrderTransaction,
  ),
  [ShopifyIdType.Page]: _shopifyGidSchema(ShopifyIdType.Page),
  [ShopifyIdType.ProductImage]: _shopifyGidSchema(ShopifyIdType.ProductImage),
  [ShopifyIdType.Refund]: _shopifyGidSchema(ShopifyIdType.Refund),
  [ShopifyIdType.Shop]: _shopifyGidSchema(ShopifyIdType.Shop),
  [ShopifyIdType.StaffMember]: _shopifyGidSchema(ShopifyIdType.StaffMember),
  [ShopifyIdType.Theme]: _shopifyGidSchema(ShopifyIdType.Theme),
  [ShopifyIdType.Publication]: _shopifyGidSchema(ShopifyIdType.Publication),
  [ShopifyIdType.MarketCatalog]: _shopifyGidSchema(ShopifyIdType.MarketCatalog),
  [ShopifyIdType.AppCatalog]: _shopifyGidSchema(ShopifyIdType.AppCatalog),
  [ShopifyIdType.CompanyLocationCatalog]: _shopifyGidSchema(
    ShopifyIdType.CompanyLocationCatalog,
  ),
  [ShopifyIdType.Video]: _shopifyGidSchema(ShopifyIdType.Video),
  [ShopifyIdType.Model3D]: _shopifyGidSchema(ShopifyIdType.Model3D),
  [ShopifyIdType.ExternalVideo]: _shopifyGidSchema(ShopifyIdType.ExternalVideo),
} as const satisfies Record<ShopifyIdType, z.ZodString>;

export function shopifyGidSchema(type: ShopifyIdType) {
  return ShopifyIdSchemas[type];
}
